import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Plus, Search } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { productService } from "@/features/products/services/product-api.service";
import type {
  ProductResponseDTO,
  ProductSearchParams,
} from "@/features/products/types/product.types";
import { ProductType } from "@/shared/types/enums";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";
import { toast } from "sonner";
import { ProductsTable } from "@/features/products/components/ProductsTable";
import { ProductFormDialog } from "@/features/products/components/ProductFormDialog";
import { ProductDeleteDialog } from "@/features/products/components/ProductDeleteDialog";
import { ProductImageDialog } from "@/features/products/components/ProductImageDialog";
import {
  fileToBase64,
  fileToBase64WithPrefix,
  isValidImageFile,
  isValidFileSize,
  getProductTypeLabel,
} from "@/features/products/utils/product.utils";

const DIALOG_MODE = {
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
  VIEW_IMAGE: "view-image",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

interface ProductSearchFilters {
  brand: string[];
  type: ProductType[];
}

const productSchema = z.object({
  type: z.enum([
    ProductType.JACKET,
    ProductType.VEST,
    ProductType.GOWN,
    ProductType.PANTS,
  ]),
  brand: z.string().min(2, "Marca deve ter pelo menos 2 caracteres"),
  color: z.string().min(2, "Cor deve ter pelo menos 2 caracteres"),
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  description: z.string().optional(),
  photo: z.string().optional(),
});

type ProductForm = z.infer<typeof productSchema>;

export function ProdutosPage() {
  const [products, setProducts] = useState<ProductResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [searchFilters, setSearchFilters] = useState<ProductSearchFilters>({
    brand: [],
    type: [],
  });
  const [brandInput, setBrandInput] = useState("");

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductResponseDTO | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);

  const form = useForm<ProductForm>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      type: ProductType.JACKET,
      brand: "",
      color: "",
      name: "",
      description: "",
      photo: "",
    },
  });

  const loadProducts = useCallback(
    async (page: number = 0, filters?: ProductSearchFilters) => {
      setLoading(true);
      try {
        const params: ProductSearchParams = {
          page,
          size: pageSize,
          sortBy: "createdAt",
          sortDirection: "DESC",
        };

        if (filters) {
          if (filters.brand.length > 0) {
            params.brand = filters.brand;
          }
          if (filters.type.length > 0) {
            params.type = filters.type;
          }
        }

        const response = await productService.list(params);
        setProducts(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setCurrentPage(page);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao carregar produtos"));
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    // Mount only — carga inicial. Padrão legado Família A;
    // migrar para useProductList em refator futuro.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadProducts(0, { brand: [], type: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const search = searchTerm.toLowerCase();
    return products.filter(
      (product) =>
        product.type.toLowerCase().includes(search) ||
        product.brand.toLowerCase().includes(search) ||
        product.color.toLowerCase().includes(search) ||
        product.name?.toLowerCase().includes(search) ||
        product.description?.toLowerCase().includes(search),
    );
  }, [products, searchTerm]);

  const handleOpenDialog = useCallback(
    (mode: DialogMode, product?: ProductResponseDTO) => {
      setDialogMode(mode);
      setDialogContent(mode);
      setSelectedProduct(product || null);

      if (mode === DIALOG_MODE.EDIT && product) {
        form.reset({
          type: product.type,
          brand: product.brand,
          color: product.color,
          name: product.name || "",
          description: product.description || "",
          photo: "",
        });
        if (product.photoUrl) {
          setPhotoPreview(
            `${product.photoUrl}?t=${new Date(product.updatedAt).getTime()}`,
          );
        }
        setPhotoRemoved(false);
      } else if (mode === DIALOG_MODE.CREATE) {
        form.reset({
          type: ProductType.JACKET,
          brand: "",
          color: "",
          name: "",
          description: "",
          photo: "",
        });
        setPhotoPreview(null);
        setPhotoRemoved(false);
      }
    },
    [form],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogMode(null);
    setTimeout(() => {
      setDialogContent(null);
      setSelectedProduct(null);
      setPhotoPreview(null);
      setPhotoRemoved(false);
      form.reset();
    }, 300);
  }, [form]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!isValidImageFile(file)) {
        toast.error("Por favor, selecione apenas arquivos de imagem");
        return;
      }

      if (!isValidFileSize(file, 5)) {
        toast.error("A imagem deve ter no máximo 5MB");
        return;
      }

      try {
        const [base64Data, base64WithPrefix] = await Promise.all([
          fileToBase64(file),
          fileToBase64WithPrefix(file),
        ]);
        form.setValue("photo", base64Data);
        setPhotoPreview(base64WithPrefix);
        setPhotoRemoved(false);
      } catch {
        toast.error("Erro ao processar a imagem");
      }
    },
    [form],
  );

  const handleRemovePhoto = useCallback(() => {
    form.setValue("photo", "");
    setPhotoPreview(null);
    setPhotoRemoved(true);
  }, [form]);

  const handleViewImage = useCallback((product: ProductResponseDTO) => {
    setSelectedProduct(product);
    setDialogMode(DIALOG_MODE.VIEW_IMAGE);
    setDialogContent(DIALOG_MODE.VIEW_IMAGE);
  }, []);

  const onSubmit = useCallback(
    async (data: ProductForm) => {
      setLoading(true);
      try {
        if (dialogMode === DIALOG_MODE.CREATE) {
          const newProduct = await productService.create({
            type: data.type,
            brand: data.brand,
            color: data.color,
            name: data.name,
            description: data.description,
            photo: data.photo,
          });
          toast.success("Produto criado com sucesso!");

          setProducts((prev) => [newProduct, ...prev]);
          setTotalElements((prev) => prev + 1);
        } else if (dialogMode === DIALOG_MODE.EDIT && selectedProduct) {
          const photoValue = photoRemoved ? null : data.photo || "";

          const updatedProduct = await productService.update(
            selectedProduct.id,
            {
              type: data.type,
              brand: data.brand,
              color: data.color,
              name: data.name,
              description: data.description,
              photo: photoValue,
            },
          );
          toast.success("Produto atualizado com sucesso!");

          setProducts((prevProducts) =>
            prevProducts.map((p) =>
              p.id === updatedProduct.id ? updatedProduct : p,
            ),
          );
        }
        handleCloseDialog();
      } catch (error: unknown) {
        if (!applyFieldErrorsToForm(error, form)) {
          toast.error(getApiErrorMessage(error, "Erro ao salvar produto"));
        }
      } finally {
        setLoading(false);
      }
    },
    [dialogMode, selectedProduct, handleCloseDialog, photoRemoved, form],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedProduct) return;

    setLoading(true);
    try {
      await productService.delete(selectedProduct.id);
      toast.success("Produto excluído com sucesso!");

      const updatedProducts = products.filter(
        (p) => p.id !== selectedProduct.id,
      );
      setProducts(updatedProducts);

      const newTotalElements = totalElements - 1;
      setTotalElements(newTotalElements);

      const newTotalPages = Math.ceil(newTotalElements / pageSize);
      setTotalPages(newTotalPages);

      if (updatedProducts.length === 0 && currentPage > 0) {
        await loadProducts(currentPage - 1);
      }

      handleCloseDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir produto"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedProduct,
    products,
    totalElements,
    pageSize,
    currentPage,
    handleCloseDialog,
    loadProducts,
  ]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        loadProducts(newPage, searchFilters);
      }
    },
    [totalPages, searchFilters, loadProducts],
  );

  const handleAddBrand = useCallback(() => {
    if (brandInput.trim() && !searchFilters.brand.includes(brandInput.trim())) {
      setSearchFilters((prev) => ({
        ...prev,
        brand: [...prev.brand, brandInput.trim()],
      }));
      setBrandInput("");
    }
  }, [brandInput, searchFilters.brand]);

  const handleRemoveBrand = useCallback((brand: string) => {
    setSearchFilters((prev) => ({
      ...prev,
      brand: prev.brand.filter((b) => b !== brand),
    }));
  }, []);

  const handleToggleType = useCallback((type: ProductType) => {
    setSearchFilters((prev) => {
      const hasType = prev.type.includes(type);
      return {
        ...prev,
        type: hasType
          ? prev.type.filter((t) => t !== type)
          : [...prev.type, type],
      };
    });
  }, []);

  const handleApplyFilters = useCallback(() => {
    setCurrentPage(0);
    loadProducts(0, searchFilters);
  }, [searchFilters, loadProducts]);

  const handleClearFilters = useCallback(() => {
    setSearchFilters({ brand: [], type: [] });
    setBrandInput("");
    setCurrentPage(0);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Produtos</h1>
          <p className="text-muted-foreground">
            Gerencie o catálogo de produtos
          </p>
        </div>
        <Can permission={ACTIONS.products.create}>
          <Button
            className="gap-2"
            onClick={() => handleOpenDialog(DIALOG_MODE.CREATE)}
            disabled={loading}
          >
            <Plus className="w-4 h-4" />
            Novo Produto
          </Button>
        </Can>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Busca</CardTitle>
          <CardDescription>Filtre produtos por marca e tipo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-2 ">
              <label className="text-sm font-medium">Marcas</label>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Digite uma marca e pressione Enter"
                  value={brandInput}
                  onChange={(e) => setBrandInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddBrand();
                    }
                  }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddBrand}
                  disabled={!brandInput.trim()}
                >
                  Adicionar
                </Button>
              </div>
              {searchFilters.brand.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {searchFilters.brand.map((brand) => (
                    <Badge
                      key={brand}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => handleRemoveBrand(brand)}
                    >
                      {brand} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Tipos de Produto</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.values(ProductType).map((type) => (
                  <Badge
                    key={type}
                    variant={
                      searchFilters.type.includes(type) ? "default" : "outline"
                    }
                    className="cursor-pointer"
                    onClick={() => handleToggleType(type)}
                  >
                    {getProductTypeLabel(type)}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:justify-between gap-2 ">
              <div className="flex items-center justify-center space-x-2">
                <Button onClick={handleApplyFilters} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Buscar
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  disabled={
                    loading ||
                    (searchFilters.brand.length === 0 &&
                      searchFilters.type.length === 0)
                  }
                >
                  Limpar Filtros
                </Button>
              </div>
              <div className="flex items-center justify-center">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                  }}
                  className="h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={loading}
                >
                  <option value={10}>10 / página</option>
                  <option value={25}>25 / página</option>
                  <option value={50}>50 / página</option>
                  <option value={100}>100 / página</option>
                </select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Lista de Produtos</CardTitle>
              <CardDescription>
                {loading
                  ? "Carregando..."
                  : `Mostrando ${filteredProducts.length} de ${totalElements} produtos`}
              </CardDescription>
            </div>
            <Input
              placeholder="Filtrar na página atual..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="md:max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading && products.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando produtos...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum produto encontrado
            </div>
          ) : (
            <ProductsTable
              products={filteredProducts}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onEdit={(product) => handleOpenDialog(DIALOG_MODE.EDIT, product)}
              onDelete={(product) =>
                handleOpenDialog(DIALOG_MODE.DELETE, product)
              }
              onViewImage={handleViewImage}
              onPageChange={handlePageChange}
            />
          )}
        </CardContent>
      </Card>

      <ProductFormDialog
        open={
          dialogMode === DIALOG_MODE.CREATE || dialogMode === DIALOG_MODE.EDIT
        }
        mode={dialogContent === DIALOG_MODE.CREATE ? "create" : "edit"}
        loading={loading}
        photoPreview={photoPreview}
        form={form}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
        onFileChange={handleFileChange}
        onRemovePhoto={handleRemovePhoto}
      />

      <ProductDeleteDialog
        open={dialogMode === DIALOG_MODE.DELETE}
        loading={loading}
        product={selectedProduct}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
      />

      <ProductImageDialog
        open={dialogMode === DIALOG_MODE.VIEW_IMAGE}
        product={selectedProduct}
        onClose={handleCloseDialog}
      />
    </div>
  );
}
